import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { ContentProvider } from "./context/ContentContext";
import { ItineraryProvider } from "./context/ItineraryContext";
import { ExtrasProvider } from "./context/ExtrasContext";
import SiteLayout from "./components/SiteLayout";

import Home from "./pages/Home";
import Accommodation from "./pages/Accommodation";
import Facilities from "./pages/Facilities";
import FacilityDetail from "./pages/FacilityDetail";
import Gallery from "./pages/Gallery";
import Experiences from "./pages/Experiences";
import Booking from "./pages/Booking";
import Reviews from "./pages/Reviews";
import Contact from "./pages/Contact";
import ReviewSubmit from "./pages/ReviewSubmit";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import ManageBooking from "./pages/ManageBooking";
import ManageLookup from "./pages/ManageLookup";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import OwnerPortal from "./pages/OwnerPortal";
import RoomDetail from "./pages/RoomDetail";
import AuthCallback from "./pages/AuthCallback";
import StaffLogin from "./pages/StaffLogin";
import StaffPortal from "./pages/StaffPortal";

function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-forest flex items-center justify-center"><Loader2 className="animate-spin text-gold" size={40} /></div>;
  }
  if (user && user.role === "owner") return <Navigate to="/owner" replace />;
  if (!user || (user.role !== "admin" && user.role !== "system_admin" && user.role !== "facilities_admin")) return <Navigate to="/admin/login" replace />;
  return children;
}

function ProtectedOwner({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-forest flex items-center justify-center"><Loader2 className="animate-spin text-gold" size={40} /></div>;
  }
  if (!user || (user.role !== "owner" && user.role !== "system_admin")) return <Navigate to="/admin/login" replace />;
  return children;
}

function ProtectedStaff({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen bg-forest flex items-center justify-center"><Loader2 className="animate-spin text-gold" size={40} /></div>;
  }
  if (!user || (user.role !== "staff" && user.role !== "system_admin")) return <Navigate to="/staff/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/accommodation" element={<Accommodation />} />
        <Route path="/facilities" element={<Facilities />} />
        <Route path="/facilities/:idx" element={<FacilityDetail />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/rooms/:slug" element={<RoomDetail />} />
        <Route path="/experiences" element={<Experiences />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/contact" element={<Contact />} />
      </Route>
      <Route path="/review/:token" element={<ReviewSubmit />} />
      <Route path="/manage" element={<ManageLookup />} />
      <Route path="/manage/:token" element={<ManageBooking />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />
      <Route path="/owner" element={<ProtectedOwner><OwnerPortal /></ProtectedOwner>} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff" element={<ProtectedStaff><StaffPortal /></ProtectedStaff>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ContentProvider>
          <ItineraryProvider>
            <ExtrasProvider>
              <BrowserRouter>
                <Toaster position="top-right" richColors />
                <AppRouter />
              </BrowserRouter>
            </ExtrasProvider>
          </ItineraryProvider>
        </ContentProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
